import React, { createContext, useContext, useRef, useCallback } from 'react';

const RefreshContext = createContext(null);

export function RefreshProvider({ children }) {
  // Stack, not a single slot — lets a component nested inside a page (e.g. a
  // fullscreen overlay) temporarily take over pull-to-refresh while it's
  // open, without permanently wiping out the page's own handler once the
  // overlay closes. Whoever registered most recently (top of stack) wins.
  const stackRef = useRef([]);

  const register = useCallback((fn) => {
    stackRef.current = [...stackRef.current, fn];
    return () => {
      stackRef.current = stackRef.current.filter((f) => f !== fn);
    };
  }, []);

  const refresh = useCallback(async () => {
    const active = stackRef.current[stackRef.current.length - 1];
    if (active) await active();
  }, []);

  return (
    <RefreshContext.Provider value={{ register, refresh }}>
      {children}
    </RefreshContext.Provider>
  );
}

/**
 * Call this in any page to register its refresh function for pull-to-refresh.
 *
 * Usage:
 *   usePageRefresh(refreshFeed);
 */
export function usePageRefresh(fn) {
  const ctx = useContext(RefreshContext);
  React.useEffect(() => {
    if (!ctx || !fn) return;
    return ctx.register(fn);
  }, [ctx, fn]);
}

export function useRefresh() {
  return useContext(RefreshContext);
}
